/**
 * Teams on one machine, as the Teams section shows them: the same names and member words, each organisation team a
 * link to its place in Teams. What the machine holds comes from its last report (`device.teams`), so the list reads
 * the same while it is away; whether each is running, and starting or stopping one, come from the machine itself.
 */

import type { DeviceTeam } from '@aioe/shared';
import { Persons } from '@gravity-ui/icons';

import { DeviceTeamRow, useTeamProjectNames } from '../../components/teams/TeamsOnMachine.tsx';
import { ArtEmpty, Button, ErrorNote, ListGroup, StatusMark } from '../../components/ui.tsx';
import type { Team, Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

const tone = 'violet' as const;

export function MachineTeamsView({ w }: { w: Workbench }) {
  const d = w.device.data;
  const held = d?.teams ?? [];
  const running = w.useView<Team[]>('teams.list', undefined, [], 60_000);
  const projectName = useTeamProjectNames(held);
  const liveOf = (t: DeviceTeam) => running.data?.find((r) => r.id === t.id) ?? running.data?.find((r) => r.name === t.name);
  const sorted = [...held].sort((a, b) => a.name.localeCompare(b.name));
  const shared = sorted.filter((t) => t.scope !== 'local');
  const groups = [
    { title: 'Teams', description: 'Shared teams this machine holds; start or stop one here.', rows: shared.filter((t) => t.kind !== 'specialist') },
    { title: 'Specialists', description: 'The specialists this machine holds.', rows: shared.filter((t) => t.kind === 'specialist') },
    { title: 'Local to this machine', description: 'Teams kept on this machine only, by choice or not shared yet.', rows: sorted.filter((t) => t.scope === 'local') },
  ].filter((g) => g.rows.length);

  const status = (t: DeviceTeam) => {
    const r = liveOf(t);
    return r ? <StatusMark state={r.running ? 'working' : 'idle'} label={r.running ? 'Running' : 'Stopped'} /> : undefined;
  };
  const control = (t: DeviceTeam) => {
    const r = liveOf(t);
    if (!r || t.kind === 'specialist') return undefined;
    return (
      <Button size="sm" variant={r.running ? 'danger-soft' : 'secondary'} onPress={() => void w.act(r.running ? 'Stop team' : 'Start team', () => w.call(r.running ? 'teams.stop' : 'teams.start', { teamId: r.id }), running.reload)}>
        {r.running ? 'Stop' : 'Start'}
      </Button>
    );
  };

  return (
    <RemoteView narrow>
      <ErrorNote error={w.error} />
      {d?.unmanagedTeams ? (
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm">
          <StatusMark state="medium" label={`${d.unmanagedTeams === 1 ? '1 team definition' : `${d.unmanagedTeams} team definitions`} not under management yet`} />
          <span className="text-xs text-muted">The workbench asks its person to add each to the organisation or use the organisation’s team.</span>
        </p>
      ) : null}
      {groups.length ? (
        groups.map((g) => (
          <ListGroup key={g.title} title={g.title} count={g.rows.length} description={g.description}>
            {g.rows.map((t) => (
              <DeviceTeamRow key={`${t.scope}:${t.id}`} t={t} projectName={projectName} status={status(t)} control={control(t)} />
            ))}
          </ListGroup>
        ))
      ) : (
        <ArtEmpty tone={tone} icon={<Persons />} title={d?.teams ? 'No teams on this machine' : 'Its teams are not reported yet'}>
          {d?.teams ? 'Teams and specialists its person builds or uses show here.' : 'AI Workbench 0.79 and later report the teams they hold.'}
        </ArtEmpty>
      )}
    </RemoteView>
  );
}
